import { useState } from 'react';
import { DEFAULT_FILTERS, FILTER_VALUES, QUERY_BEHAVIOR } from '../../constants';
import { minUtrValue, maxUtrValue } from '../../../../../utils/constants';

const { EVENTS, OPEN_REGISTRATION, VERIFIED, SINGLES, DEFAULT_DISTANCE } = FILTER_VALUES;

export const useEventFilters = (initialFilters = {}) => {
  // Fields that should respect explicit null values from initialFilters

  // Create null overrides for fields that are explicitly set to null
  const nullOverrides = QUERY_BEHAVIOR.FIELDS_RESPECTING_NULL.reduce((overrides, field) => {
    if (initialFilters[field] === null) {
      overrides[field] = null;
    }
    return overrides;
  }, {});

  // Remove UI-only field from filter state while preserving it for the input
  const { locationInputValue: initialLocationInputValue, ...initialFiltersWithoutInput } = initialFilters || {};

  const [filters, setFilters] = useState({
    ...DEFAULT_FILTERS,
    ...initialFiltersWithoutInput,
    ...nullOverrides,
  });

  // Separate state for location input (what user is typing/selecting)
  const [locationInputValue, setLocationInputValue] = useState(initialLocationInputValue || '');

  // Temporary storage for geocoded location data (ready to apply but not applied yet)
  const [pendingLocationData, setPendingLocationData] = useState(null);

  const updateFilters = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const updateLocationInput = (value) => {
    setLocationInputValue(value);
  };

  const setPendingLocation = (locationData) => {
    setPendingLocationData(locationData);
  };

  const applyPendingLocation = () => {
    if (pendingLocationData) {
      // Merge pending location with current filters and preserve user-selected distance
      const mergedFilters = { ...filters, ...pendingLocationData };
      mergedFilters.distance = filters.distance || pendingLocationData.distance || DEFAULT_DISTANCE;

      setPendingLocationData(null);
      setFilters(mergedFilters);
      return mergedFilters;
    }
    return filters;
  };

  const replaceFilters = (newFilters) => {
    setFilters({ ...newFilters });
  };

  const clearFilters = () => {
    // Preserve current location data if it exists
    const currentLocationData = {
      location: filters.location,
      lat: filters.lat,
      lng: filters.lng,
      distance: filters.distance || DEFAULT_DISTANCE,
      locationCountryName: filters.locationCountryName,
      locationStateName: filters.locationStateName,
    };

    const clearedFilters = {
      type: EVENTS,
      eventState: OPEN_REGISTRATION,
      dateRange: null, // Clear dateRange (mutually exclusive with eventState)
      startDate: null, // Clear custom date fields
      endDate: null,
      utrMin: minUtrValue,
      utrMax: maxUtrValue,
      utrType: VERIFIED,
      utrTeamType: SINGLES,
      sportTypes: filters.sportTypes, // Preserve sport types
      // Preserve location data if it exists
      ...currentLocationData,
      tours: {},
      eventTypes: {},
      eventRegions: {},
      eventTags: {},
      eventUtrTypes: {},
      eventTeamTypes: {},
      genders: {},
      ballTypes: {},
    };

    setFilters(clearedFilters);
    // Don't clear location input if we have location data
    if (!filters.location && !filters.locationCountryName) {
      setLocationInputValue('');
    }
    setPendingLocationData(null); // Clear pending data
    return clearedFilters;
  };

  return {
    filters,
    locationInputValue,
    pendingLocationData,
    updateFilters,
    updateLocationInput,
    setPendingLocation,
    applyPendingLocation,
    replaceFilters,
    clearFilters,
  };
};
